import { useEffect, useRef, useCallback, useState } from "react";
import { Box, Typography } from "@mui/material";
import { RichTextEditor } from "mui-tiptap";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { FontFamily } from "@tiptap/extension-font-family";
import TextAlign from "@tiptap/extension-text-align";
import {
  MenuControlsContainer,
  MenuSelectHeading,
  MenuDivider,
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonUnderline,
  MenuButtonBulletedList,
  MenuButtonOrderedList,
  MenuButtonAlignLeft,
  MenuButtonAlignCenter,
  MenuButtonAlignRight,
  MenuButtonAlignJustify,
  MenuButtonUndo,
  MenuButtonRedo,
  MenuSelectFontFamily,
  ColorSwatchButton,
} from "mui-tiptap";

interface DescriptionEditorProps {
  value: string;
  onChange: (val: string) => void;
}

const DescEditor = ({ value, onChange }: DescriptionEditorProps) => {
  const editorRef = useRef<any>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Handle editor creation
  const handleCreate = useCallback(({ editor }: { editor: any }) => {
    editorRef.current = editor;
    setIsInitialized(true);
  }, []);

  // Sync external value changes - FIXED: Use a key to force re-initialization
  useEffect(() => {
    if (!isInitialized || !editorRef.current) return;

    try {
      const currentContent = editorRef.current.getHTML();
      // Check if content is actually different
      if (value !== currentContent) {
        editorRef.current.commands.setContent(value || "<p></p>", false);
      }
    } catch (error) {
      console.error("Error syncing editor content:", error);
    }
  }, [value, isInitialized]);

  const fontOptions = [
    { label: "Default", value: "" },
    { label: "Arial", value: "Arial, sans-serif" },
    { label: "Times New Roman", value: "'Times New Roman', serif" },
    { label: "Courier New", value: "'Courier New', monospace" },
    { label: "Georgia", value: "Georgia, serif" },
    { label: "Verdana", value: "Verdana, sans-serif" },
    { label: "Tahoma", value: "Tahoma, sans-serif" },
    { label: "Trebuchet MS", value: "'Trebuchet MS', sans-serif" },
    { label: "Comic Sans MS", value: "'Comic Sans MS', cursive" },
    { label: "Impact", value: "Impact, fantasy" },
  ];

  // Define your custom colors
  const customColors = [
    { value: "#000000", label: "Black" },
    { value: "#eb3c96", label: "Pink" },
    { value: "rgba(81, 35, 132, 1)", label: "Purple" },
  ];

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Description
      </Typography>

      <RichTextEditor
        
        onCreate={handleCreate}
        content={value || "<p></p>"}
        onUpdate={({ editor }) => {
          const newContent = editor.getHTML();
          if (newContent !== value) {
            onChange(newContent);
          }
        }}
        extensions={[
          StarterKit,
          TextStyle,
          Color,
          FontFamily.configure({
            types: ["textStyle"],
          }),
          TextAlign.configure({
            types: ["heading", "paragraph"],
            alignments: ["left", "center", "right", "justify"],
            defaultAlignment: "left",
          }),
        ]}
        renderControls={() => (
          <MenuControlsContainer>
            {/* Headings */}
            <MenuSelectHeading />
            <MenuDivider />

            {/* Font Family */}
            <MenuSelectFontFamily options={fontOptions} />
            <MenuDivider />

            {/* Basic formatting */}
            <MenuButtonBold />
            <MenuButtonItalic />
            <MenuButtonUnderline />
            <MenuDivider />

            {/* Custom Color Swatches Only */}
            {customColors.map((color) => (
              <ColorSwatchButton
                key={color.value}
                value={color.value}
                label={color.label}
                onClick={() => {
                  if (editorRef.current) {
                    editorRef.current
                      .chain()
                      .focus()
                      .setColor(color.value)
                      .run();
                  }
                }}
              />
            ))}
            <MenuDivider />

            {/* Lists */}
            <MenuButtonBulletedList />
            <MenuButtonOrderedList />
            <MenuDivider />

            {/* Text alignment */}
            <MenuButtonAlignLeft />
            <MenuButtonAlignCenter />
            <MenuButtonAlignRight />
            <MenuButtonAlignJustify />
            <MenuDivider />

            {/* Undo / Redo */}
            <MenuButtonUndo />
            <MenuButtonRedo />
          </MenuControlsContainer>
        )}
      />
    </Box>
  );
};

export default DescEditor;
